import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';

import { TableStoreContext, TableStore, TableStoreApi, createTableStore } from '@store/TableStore';
import { Table, TableProps } from '@components/table/Table';
import { TableFilter, TFilterValues, TTableBase } from '@components/table/types';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { StoreTableToolbar } from '@components/table/StoreTable/StoreTableToolbar';
import { QuickFiltersProps } from '@components/table/QuickFilters';
import { useSearchParams } from 'react-router-dom';
import { isValid } from 'date-fns';
import { CustomFilterToolbarProps } from '@components/table/TableCreateFiltersModal/CustomFilterToolbar/CustomFilterToolbar';
import { enqueueSnackbar } from 'notistack';
import { getDefaultEnvironment } from '@utils/api-client';
import { Environment } from '@/config';
import { areArraysEqual } from '@utils/arrays';
import { getQueryParamForAppliedFilter } from '@components/table/TableCreateFiltersModal/getQueryParamForAppliedFilter';
import { ensureValueIsADate } from '@components/datePicker/DateRangePicker';

interface TableWithStoreProps<T extends TTableBase> {
  tableId: string;
  tableProps: TableProps<T>;
  storeProps: {
    columnSelectorConfig: {
      columnSelector: Record<string, Record<string, string>>;
      defaultHiddenColumns?: Record<string, boolean>;
      defaultDisabledKey: string;
      selectedTab: string;
      hiddenFromSelectionKeys?: string[];
    } | null;
    quickFilterGroup?: QuickFiltersProps['quickFilterGroup'];
    tableFilterTypeMetaDataBuilder?: CustomFilterToolbarProps['tableFilterTypeMetaDataBuilder'];
  };
}

const showInfoLogs = getDefaultEnvironment() !== Environment.Prod;

export function TableWithStore<T extends TTableBase>({ tableProps, storeProps, tableId }: TableWithStoreProps<T>) {
  const storeRef = useRef<TableStoreApi>(null);
  if (!storeRef.current) {
    storeRef.current = createTableStore({
      tableId,
      hiddenColumns: storeProps?.columnSelectorConfig?.defaultHiddenColumns,
    });
  }

  return (
    <TableStoreContext.Provider value={storeRef.current}>
      <TableWithStoreBody tableId={tableId} tableProps={tableProps} storeProps={storeProps} />
    </TableStoreContext.Provider>
  );
}
export const useTableStore = <T,>(selector: (store: TableStore) => T): T => {
  const tableStoreContext = useContext(TableStoreContext);

  if (!tableStoreContext) {
    throw new Error(`useTableStore must be used within TableWithStore`);
  }

  return useStoreWithEqualityFn(tableStoreContext, selector, shallow);
};

export function TableWithStoreBody<T extends TTableBase>({
  tableId,
  storeProps: { columnSelectorConfig, quickFilterGroup, tableFilterTypeMetaDataBuilder },
  tableProps,
}: TableWithStoreProps<T>) {
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();
  const store = useContext(TableStoreContext);
  const [readFromUrl, setReadFromUrl] = useState(false);

  if (!store) throw new Error('Missing TableStoreContext.Provider in the tree');

  const [
    hiddenColumns,
    queryString,
    updateQueryParams,
    updateAppliedFilters,
    appliedFilters,
    pinnedFilters,
    togglePinnedFilters,
    pinnedFiltersEnabled,
    filtersLoaded,
  ] = useTableStore((state) => [
    state.hiddenColumns,
    state.queryParams,
    state.updateQueryParams,
    state.updateAppliedFilters,
    state.appliedFilters,
    state.pinnedFilters,
    state.togglePinnedFilters,
    state.pinnedFiltersEnabled,
    state.filtersLoaded,
  ]);

  const updateCustomFilter = (updatedFilters: TFilterValues) => {
    const newFilters = { ...appliedFilters, ...updatedFilters };
    updateAppliedFilters(newFilters);
  };

  const [storeTableToolbarHeight, setTableToolbarHeight] = useState(0);

  useEffect(() => {
    setTimeout(() => {
      // ensure the toolbar has had enough time to render the loaded filters
      const height = document.getElementById('store-table-toolbar')?.offsetHeight ?? 0;
      setTableToolbarHeight(height);
    }, 200);
  }, [filtersLoaded]);

  const adjustTableHeaderHeight = () => {
    const height = document.getElementById('store-table-toolbar')?.offsetHeight ?? 0;
    setTableToolbarHeight(height);
  };

  useEffect(() => {
    adjustTableHeaderHeight();
  }, [appliedFilters, pinnedFilters, appliedFilters?.length, pinnedFilters?.length]);

  const selectedTab = columnSelectorConfig?.selectedTab ?? '';
  const selectedColumns = columnSelectorConfig?.columnSelector?.[selectedTab] ?? {};

  const selectedColumnVisibility = Object.keys(selectedColumns).reduce(
    (acc, key) => {
      acc[key] = hiddenColumns?.[key] !== true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const { sortValue: overrideSortValues, filters: overrideFilterValues } = useMemo(() => {
    // On table load check if we need to pull in values from the URL or store
    const sortValueInUrl = searchParamsInTheUrl.get('sort');
    const filters: any = {};

    let sortValue;
    if (sortValueInUrl) {
      const [field, sort] = sortValueInUrl.split(',');
      sortValue = { field, sort: sort as 'asc' | 'desc' };
    }

    searchParamsInTheUrl.forEach((value, key) => {
      if (key !== 'sort' && key !== 'debugMode') {
        filters[key] = value;
      }
    });

    if (Object.keys(filters).length === 0 && !sortValue) {
      return { sortValue: queryString?.sortValue, filters: queryString?.filters };
    }

    return { sortValue, filters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterConfigDictionary = useMemo(() => {
    return tableProps.filterConfig?.filters?.reduce((acc, filter) => {
      acc[filter.field] = filter;
      return acc;
    }, {} as any);
  }, [tableProps.filterConfig?.filters]);

  useEffect(() => {
    // update URL to match values loaded from the store, if applicable
    if ((overrideSortValues || overrideFilterValues) && queryString) {
      if (overrideSortValues) {
        searchParamsInTheUrl.set('sort', `${overrideSortValues.field},${overrideSortValues.sort}`);
      }
      Object.entries(overrideFilterValues).forEach(([key, value]) => {
        if (filterConfigDictionary?.[key]?.type === TableFilter.DATE_RANGE) {
          let range = value as { min?: string; max?: string };
          if (typeof value === 'string') {
            const [min, max] = value.split(',');
            range = { min: undefined, max: undefined };
            if (min !== 'null') {
              const minDate = new Date(min);
              if (isValid(minDate)) {
                range.min = minDate.toISOString();
              }
            }

            if (max !== 'null') {
              // Changed from max === 'null' to max !== 'null'
              const maxDate = new Date(max);
              if (isValid(maxDate)) {
                range.max = maxDate.toISOString();
              }
            }
          }

          const queryParam = `${range?.min ? new Date(range.min).toISOString() : null},${range?.max ? new Date(range.max).toISOString() : null}`;
          searchParamsInTheUrl.set(key, queryParam as string);
        } else if (filterConfigDictionary?.[key]?.type === TableFilter.NUMBER_RANGE) {
          let range = value as { min?: string; max?: string };
          if (typeof value === 'string') {
            const [min, max] = value.split(',');
            range = { min: undefined, max: undefined };
            if (min !== 'null') {
              if (isValidNumber(min)) {
                range.min = min;
              }
            }

            if (max !== 'null') {
              if (isValidNumber(max)) {
                range.max = max;
              }
            }
          }

          const queryParam = `${range?.min ?? null},${range?.max ?? null}`;
          searchParamsInTheUrl.set(key, queryParam as string);
        } else {
          // Values rehydrated from the persisted store can be option objects or
          // arrays of them (e.g. multi-select Policy Status). Serialize via the
          // shared helper so they map back to their raw `.value` strings instead
          // of being stringified to "[object Object]".
          const queryParam = getQueryParamForAppliedFilter(key, value);
          if (queryParam !== null) {
            searchParamsInTheUrl.set(key, queryParam);
          } else {
            searchParamsInTheUrl.delete(key);
          }
        }
      });

      setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideFilterValues, overrideSortValues]);

  useEffect(() => {
    // update store when URL changes
    const sortValueInUrl = searchParamsInTheUrl.get('sort');
    const filters: any = {};

    let sortValue;
    if (sortValueInUrl) {
      const [field, sort] = sortValueInUrl.split(',');
      sortValue = { field, sort: sort as 'asc' | 'desc' };
    }

    searchParamsInTheUrl.forEach((value, key) => {
      if (filterConfigDictionary?.[key]?.type === TableFilter.DATE_RANGE) {
        const [start, end] = value.split(',');
        const startDate = start ? new Date(start) : null;
        const endDate = start ? new Date(end) : null;
        filters[key] = {
          min: isValid(startDate) ? startDate : null,
          max: isValid(endDate) ? endDate : null,
          type: TableFilter.DATE_RANGE,
        };
      } else if (filterConfigDictionary?.[key]?.type === TableFilter.NUMBER_RANGE) {
        const [start, end] = value.split(',');
        const startNumber = start ?? null;
        const endNumber = end ?? null;
        filters[key] = {
          min: isValidNumber(startNumber) ? startNumber : null,
          max: isValidNumber(endNumber) ? endNumber : null,
          type: TableFilter.NUMBER_RANGE,
        };
      } else if (filterConfigDictionary?.[key]?.fieldConfig?.selectField?.multiSelect) {
        const splitValues = value.split(',');
        // For multi-selects backed by static options (e.g. Policy Status), map the
        // raw URL strings back to their option objects so labels/selection restore
        // correctly on a cold load. Async typeaheads have no static options here and
        // keep the raw string values (their labels resolve via entity metadata).
        const staticOptions = filterConfigDictionary?.[key]?.other?.options;
        filters[key] = staticOptions?.length
          ? splitValues.map((v: string) => staticOptions.find((o: { value: string }) => o.value === v) ?? v)
          : splitValues;
      } else {
        filters[key] = value;
      }
    });

    updateQueryParams({ sortValue, filters });

    // if there is a key in appliedFilters that matches a key filters
    // if appliedFilter.value = filers
    // then use appliedFilter to preserve the label value
    const newFilters = Object.entries(filters).reduce((acc: any, [key, value]: any) => {
      try {
        if (value === appliedFilters?.[key]?.value) {
          acc[key] = appliedFilters?.[key];
        } else if (filterConfigDictionary?.[key]?.fieldConfig?.selectField?.multiSelect) {
          // handle preserving label for mutliselect values as well

          if (typeof appliedFilters?.[key] === 'string') {
            // old unsupported logic, prevent from crashing
            acc[key] = undefined;
          }

          const valueIds = value.map((v: string | { value: string }) => (typeof v === 'string' ? v : v.value));
          const appliedFilterIds = appliedFilters?.[key]?.map((v: any) => v.value);

          if (appliedFilterIds && appliedFilterIds.length > 0 && areArraysEqual(valueIds ?? [], appliedFilterIds)) {
            acc[key] = appliedFilters?.[key];
          } else {
            acc[key] = value;
          }
        } else {
          acc[key] = value;
        }

        return acc;
      } catch (e) {
        // clear filter if an error rather than breaking
        if (showInfoLogs) {
          console.info(`Error processing filter ${key}: ${e}`);
        }
        acc[key] = undefined;
        return acc;
      }
    }, {});

    updateAppliedFilters(newFilters);

    const filterKeys = Object.keys(filters);
    // remove keys that are not relevant to the table
    const relevantFilterKeys = filterKeys.filter((key) =>
      tableProps.filterConfig?.filters?.some((filter) => filter.field === key),
    );

    const isClearUrl = relevantFilterKeys.length === 0;

    if (isClearUrl && !readFromUrl) {
      if (showInfoLogs) {
        // console.info('The url is "clean" - enabling all pinned filters');

        // need to do query param for all pinned filters
        Object.entries(pinnedFilters).forEach(([key, value]: any) => {
          const queryParam = getQueryParamForAppliedFilter(key, value);
          if (queryParam !== null) {
            searchParamsInTheUrl.set(key, queryParam);
          }
        });
        setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
      }
      // enable and turn on all pinned filters
      updateAppliedFilters(pinnedFilters);
      togglePinnedFilters(true);
    }

    if (pinnedFiltersEnabled) {
      let disablePinnedFilers = false;

      let adjustedFilters = { ...newFilters };
      // if any pinned filters are in new filters and the values dont match alert hi
      Object.entries(pinnedFilters).forEach(([key, value]: any) => {
        if (!newFilters[key]) {
          // remove filter from adjusted list - it wont be included if pinned filters get turned off
          adjustedFilters[key] = undefined;
          return;
        }
        const newFilterValue = newFilters[key];

        if (filterConfigDictionary?.[key]?.type === TableFilter.DATE_RANGE) {
          // compare date values
          const newStartDate = ensureValueIsADate(newFilterValue?.min ?? null)?.toISOString();
          const newEndDate = ensureValueIsADate(newFilterValue?.max ?? null)?.toISOString();
          const pinnedStartDate = ensureValueIsADate(value?.min)?.toISOString();
          const pinnedEndDate = ensureValueIsADate(value?.max)?.toISOString();

          if (newStartDate !== pinnedStartDate || newEndDate !== pinnedEndDate) {
            if (showInfoLogs) {
              console.info(`Date Pinned filter ${key} date range does not match the new filter value`);
            }
            disablePinnedFilers = true;
          }
        } else if (filterConfigDictionary?.[key]?.type === TableFilter.NUMBER_RANGE) {
          // compare date values
          const newStartNumber = newFilterValue?.min?.toString();
          const newEndNumber = newFilterValue?.max?.toString();
          const pinnedStartNumber = value?.min?.toString();
          const pinnedEndNumber = value?.max?.toString();

          if (newStartNumber !== pinnedStartNumber || newEndNumber !== pinnedEndNumber) {
            if (showInfoLogs) {
              console.info(`Number Range Pinned filter ${key} date range does not match the new filter value`);
              adjustedFilters[key] = value;
            }
            disablePinnedFilers = true;
          }
        } else if (Array.isArray(newFilterValue)) {
          // multi select handler
          const pinnedIdValues = value.map((v: any) => v.value ?? v);
          newFilterValue.forEach((newFilterValueItem: any) => {
            if (!pinnedIdValues.includes(newFilterValueItem?.value ?? newFilterValueItem)) {
              if (showInfoLogs) {
                console.info(`Array Pinned filter ${key} does not match the new filter value`);
                adjustedFilters[key] = value;
              }
              disablePinnedFilers = true;
            }
          });
        } else {
          // handle standard values
          const idValue = typeof newFilterValue === 'string' ? newFilterValue : newFilterValue?.value;
          const pinnedIdValue = typeof value === 'string' ? value : value?.value;

          if (idValue !== pinnedIdValue) {
            disablePinnedFilers = true;

            if (showInfoLogs) {
              console.info(`Pinned filter ${key} does not match the new filter value`);
              adjustedFilters[key] = value;
            }
          }
        }
      });

      if (disablePinnedFilers) {
        // clear all pinnedFilters from newFilters
        enqueueSnackbar('Pinned filters have been disabled due to shared URL', {
          variant: 'warning',
          preventDuplicate: true,
        });

        togglePinnedFilters(false);

        // for all filters in appliedFilters
        // if not in adjustedFilers set to undefined
        Object.entries(appliedFilters ?? {}).forEach(([key]: any) => {
          if (!adjustedFilters[key]) {
            adjustedFilters[key] = undefined;
          }
        });

        updateAppliedFilters(adjustedFilters);
      }
    }
    setReadFromUrl(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsInTheUrl]);

  const cleanQuickFilters = (filters: TFilterValues) => {
    // used to ensure that the quick filters are set to the default value if the value is not in the URL and clear out any invalid values
    const df = filters;
    let setInUrl = false;

    let results = quickFilterGroup?.reduce((acc, qfp) => {
      const queryValue = searchParamsInTheUrl.get(qfp.queryKey) || undefined;
      const value = queryValue ?? df?.defaultFilters?.[qfp.queryKey];

      if (value && qfp.quickFilters.find((f) => f.hidden !== true && f.value === value)) {
        acc[qfp.queryKey] = value;
      } else {
        const defaultQp = qfp.quickFilters.find((f) => f.defaultChecked);
        if (defaultQp) {
          acc[qfp.queryKey] = defaultQp.value;
          searchParamsInTheUrl.set(qfp.queryKey, defaultQp.value);
          setInUrl = true;
        } else {
          acc[qfp.queryKey] = '';
        }
      }

      return acc;
    }, {} as any);

    tableProps?.filterConfig?.filters?.forEach((filter) => {
      const queryKey = tableProps?.filterConfig?.filterQueryParam?.[filter.field] ?? undefined;

      if (typeof queryKey === 'string') {
        const queryValue = searchParamsInTheUrl.get(queryKey) || undefined;
        if (queryValue) {
          if (!results) {
            results = {};
          }

          results[queryKey] = queryValue;
        }
      }
    });

    if (setInUrl) {
      setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
    }

    return results;
  };

  const cleanedQuickFilters = useMemo(() => {
    return overrideFilterValues && Object.keys(overrideFilterValues)?.length > 0
      ? cleanQuickFilters(overrideFilterValues)
      : cleanQuickFilters(tableProps?.filterConfig?.defaultFilters ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideFilterValues, tableProps?.filterConfig?.defaultFilters]);

  const defaultQuickFilters = useMemo(() => {
    return quickFilterGroup?.reduce((acc, qfp) => {
      const defaultValue = qfp.quickFilters.find((f) => f.defaultChecked)?.value;
      if (defaultValue) {
        acc[qfp.queryKey] = defaultValue;
      }
      return acc;
    }, {} as any);
  }, [quickFilterGroup]);

  const headerHeight = document.getElementById('header-wrapper')?.offsetHeight ?? 0;
  const apiEnvBanner = document.getElementById('api-env-banner')?.offsetHeight ?? 0;
  const tableHeaderHeight = 100;
  const totalHeaderHeight = headerHeight + storeTableToolbarHeight + apiEnvBanner + tableHeaderHeight;

  return (
    <Box
      data-testid={tableId}
      sx={{
        width: '100%',
        height: '100%',
        borderTopRightRadius: 8,
        backgroundColor: 'background.light',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
      }}
      id={tableId}
    >
      <StoreTableToolbar<T>
        isLoading={!readFromUrl}
        updateCustomFilters={updateCustomFilter}
        tableFilterTypeMetaDataBuilder={tableFilterTypeMetaDataBuilder}
        columns={tableProps?.columns}
        columnSelectorConfig={columnSelectorConfig}
        quickFilterGroup={quickFilterGroup}
        selectedTab={selectedTab}
        filterConfig={{
          ...tableProps.filterConfig,
          filterQueryParam: tableProps.filterConfig?.filterQueryParam ?? null,
          defaultFilters: cleanedQuickFilters,
        }}
        adjustTableHeaderHeight={adjustTableHeaderHeight}
      />

      <Table<T>
        tableId={tableId}
        syncToUrl
        ariaLabel={tableProps.ariaLabel}
        isInitiating={tableProps.isInitiating}
        onLoadData={tableProps.onLoadData}
        columns={tableProps.columns}
        filterConfig={{
          filterQueryParam: tableProps.filterConfig?.filterQueryParam ?? null,
          ignoreQueryParams: tableProps.filterConfig?.ignoreQueryParams ?? [],
          defaultFilters:
            overrideFilterValues && Object.keys(overrideFilterValues)?.length > 0
              ? overrideFilterValues
              : (tableProps.filterConfig?.defaultFilters ?? {}),
          customFilters: appliedFilters,
          resetToFilters: defaultQuickFilters,
          onResetFilters: (resetValues) => {
            if (!resetValues) {
              updateAppliedFilters({});
              return;
            }

            const resetValuesKeys = Object.keys(resetValues);
            const newValues = { ...appliedFilters };

            Object.entries(newValues).forEach(([key]) => {
              if (!resetValuesKeys?.includes(key)) {
                delete newValues[key];
              }
            });

            updateAppliedFilters(newValues);
          },
        }}
        sortConfig={{
          ...tableProps.sortConfig,
          sortQueryParam: tableProps.sortConfig?.sortQueryParam ?? null,
          defaultSort: overrideSortValues ?? tableProps.sortConfig?.defaultSort ?? undefined,
        }}
        sxProps={{
          dataGrid: {
            ...tableProps.sxProps?.dataGrid,
          },
          tableContainer: {
            ...tableProps.sxProps?.tableContainer,
            maxHeight: `calc(100vh - ${totalHeaderHeight}px)`,
          },
          dataGridContainer: {
            ...tableProps.sxProps?.dataGridContainer,
          },
        }}
        paginationConfig={tableProps.paginationConfig}
        noResultsConfig={{
          disableNoMatchFound: false,
          ...tableProps.noResultsConfig,
          noResultsTitle: tableProps.noResultsConfig?.noResultsTitle ?? 'No matches were found',
          noResultsMessage: tableProps.noResultsConfig?.noResultsMessage,
          ComponentOverride: tableProps.noResultsConfig?.ComponentOverride,
        }}
        rowConfig={{
          rowHeight: tableProps.rowConfig?.rowHeight,
          getRowId: tableProps.rowConfig?.getRowId,
          onRowClick: tableProps.rowConfig?.onRowClick,
          getRowClassName: tableProps.rowConfig?.getRowClassName,
          getRowHeight: tableProps.rowConfig?.getRowHeight,
        }}
        columnVisibilityModel={selectedColumnVisibility}
        slotsConfig={{
          Toolbar: tableProps?.slotsConfig?.Toolbar ?? undefined,
          Pagination: tableProps?.slotsConfig?.Pagination,
        }}
      />
    </Box>
  );
}
function isValidNumber(value: string | number): boolean {
  if (typeof value === 'number') {
    return true;
  }
  const number = parseFloat(value);
  return !isNaN(number) && isFinite(number);
}
